import LumaAccessiblePanel from '../../../components/admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
// src/pages/Admin/correspondencia/ValidarEntregaModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
    Shield, KeyRound, CheckCircle2, X, Camera, 
    Upload, RefreshCw, AlertCircle, Trash2, User, FileText,
    Printer, Building2, UserCheck, PackageCheck, Send
} from 'lucide-react';
import { Correspondencia, marcarComoEntregado } from '../../../services/correspondencia';
import { useAuth } from '../../../context/AuthContext';
import { convertImageToWebP } from '../../../utils/imageCompressor';


interface ValidarEntregaModalProps {
    isOpen: boolean;
    correspondencia: Correspondencia | null;
    autoFillPin?: boolean;
    onClose: () => void;
    onSuccess: (updated: Correspondencia) => void;
}

const ValidarEntregaModal: React.FC<ValidarEntregaModalProps> = ({
    isOpen,
    correspondencia,
    autoFillPin = true,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();
    
    // Nombre del recepcionista actual logueado
    const recepcionistaNombre = user 
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Personal de Recepción'
        : 'Personal de Recepción';

    const [pinIngresado, setPinIngresado] = useState('');
    const [receptorNombre, setReceptorNombre] = useState('');
    const [receptorCiNit, setReceptorCiNit] = useState('');
    const [receptorTelefono, setReceptorTelefono] = useState('');
    
    // Fotos
    const [imagenEntregaFile, setImagenEntregaFile] = useState<File | null>(null);
    const [imagenPreview, setImagenPreview] = useState<string | null>(null);
    
    // Cámara en vivo para captura instantánea
    const [modoCamaraEnVivo, setModoCamaraEnVivo] = useState(false);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Estado para comprobante post-entrega
    const [entregaExitosaData, setEntregaExitosaData] = useState<Correspondencia | null>(null);

    // Inicializar datos
    useEffect(() => {
        if (correspondencia) {
            // Auto-llenar PIN si está habilitado o viene del escáner
            if (autoFillPin && correspondencia.pin_seguridad) {
                setPinIngresado(correspondencia.pin_seguridad);
            } else {
                setPinIngresado('');
            }

            setReceptorNombre(
                correspondencia.receptor_nombre || 
                correspondencia.cliente_nombre || 
                correspondencia.destinatario_nombre || 
                ''
            );
            setReceptorCiNit(
                correspondencia.receptor_ci_nit || 
                correspondencia.destinatario_ci_nit || 
                ''
            );
            setReceptorTelefono(
                correspondencia.receptor_telefono || 
                correspondencia.destinatario_telefono || 
                ''
            );
            setImagenEntregaFile(null);
            setImagenPreview(null);
            setModoCamaraEnVivo(false);
            setErrorMsg(null);
            setEntregaExitosaData(null);
        }
    }, [correspondencia, isOpen, autoFillPin]);

    // Detener stream de cámara al desmontar o cerrar
    const stopCameraStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    useEffect(() => {
        return () => {
            stopCameraStream();
        };
    }, []);

    // Iniciar cámara web para sacar foto
    const startLiveCamera = async () => {
        setErrorMsg(null);
        setModoCamaraEnVivo(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setModoCamaraEnVivo(false);
            setErrorMsg('No se pudo acceder a la cámara. Puedes subir una foto desde tu equipo.');
        }
    };

    // Tomar snapshot de la cámara en vivo
    const capturePhoto = () => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], `entrega_${Date.now()}.webp`, { type: 'image/webp' });
                    setImagenEntregaFile(file);
                    setImagenPreview(canvas.toDataURL('image/webp', 0.85));
                    stopCameraStream();
                    setModoCamaraEnVivo(false);
                }
            }, 'image/webp', 0.85);
        }
    };

    const cancelLiveCamera = () => {
        stopCameraStream();
        setModoCamaraEnVivo(false);
    };

    // Subir imagen desde archivo / máquina (convertida a WebP)
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const webpFile = await convertImageToWebP(file);
            setImagenEntregaFile(webpFile);
            const reader = new FileReader();
            reader.onload = () => {
                setImagenPreview(reader.result as string);
            };
            reader.readAsDataURL(webpFile);
        }
    };

    const handleRemovePhoto = () => {
        setImagenEntregaFile(null);
        setImagenPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Imprimir comprobante
    const handleImprimir = () => {
        window.print();
    };

    // Confirmar entrega
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!correspondencia) return;

        if (!pinIngresado.trim()) {
            setErrorMsg('Debe ingresar el PIN de 6 dígitos que le proporciona el cliente.');
            return;
        }

        if (!receptorNombre.trim() || !receptorCiNit.trim()) {
            setErrorMsg('Debe registrar el Nombre y CI/NIT de quien retira la correspondencia.');
            return;
        }

        setIsSubmitting(true);
        setErrorMsg(null);

        try {
            const updated = await marcarComoEntregado(correspondencia.id, {
                receptor_nombre: receptorNombre.trim(),
                receptor_apellido: '',
                receptor_telefono: receptorTelefono.trim(),
                receptor_ci_nit: receptorCiNit.trim(),
                pin_seguridad: pinIngresado.trim(),
                imagen_entrega: imagenEntregaFile
            });

            setEntregaExitosaData(updated);
            onSuccess(updated);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            const detail = err.response?.data?.detail || 'Error al validar la entrega. Verifique que el PIN sea correcto.';
            setErrorMsg(detail);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen || !correspondencia) return null;

    return (
        <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black/70 flex items-center justify-center z-[85] p-4 backdrop-blur-sm animate-fade-in font-['DM_Sans',sans-serif]">
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    #print-comprobante-entrega, #print-comprobante-entrega * {
                        visibility: visible;
                    }
                    #print-comprobante-entrega {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                }
            `}</style>

            <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[94vh] flex flex-col animate-scale-up" id="print-comprobante-entrega">
                {/* Cabecera */}
                <div className="bg-gradient-to-r from-[#3564db] to-[#92b1ed] p-5 text-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-sm">
                            {entregaExitosaData ? <PackageCheck className="w-6 h-6 text-white" /> : <Shield className="w-6 h-6 text-white" />}
                        </div>
                        <div>
                            <LumaAccessibleTitle className="text-lg font-bold">
                                {entregaExitosaData ? '¡Entrega Registrada con Éxito!' : 'Validar Entrega de Correspondencia'}
                            </LumaAccessibleTitle>
                            <p className="text-xs text-white/80">
                                Paquete <span className="font-mono font-bold bg-white/20 px-2 py-0.5 rounded">{correspondencia.codigo || `#${correspondencia.id}`}</span>
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* VISTA 1: Comprobante Final Post-Entrega */}
                {entregaExitosaData ? (
                    <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scroll text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full mb-1">
                            <CheckCircle2 className="w-10 h-10" />
                        </div>
                        
                        <div>
                            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-bold text-xs rounded-full uppercase tracking-wider">
                                Estado: Entregado
                            </span>
                            <h4 className="text-xl font-extrabold text-gray-900 mt-2">Comprobante de Entrega</h4>
                            <p className="text-xs text-gray-500">
                                {new Date(entregaExitosaData.fecha_entrega || Date.now()).toLocaleString('es-BO')}
                            </p>
                        </div>

                        {/* Banner de Notificación enviada */}
                        <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-2xl text-xs text-blue-800 flex items-center justify-center gap-2">
                            <Send className="w-4 h-4 text-[#3564db]" />
                            <span>Se ha enviado la notificación de entrega al cliente y remitente.</span>
                        </div>

                        {/* Las 3 partes claramente detalladas */}
                        <div className="space-y-2.5 text-left text-xs bg-gray-50 p-4 rounded-2xl border border-gray-100">
                            {/* 1. Remitente */}
                            <div className="pb-2 border-b border-gray-200/70">
                                <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">
                                    1. Remitente (Quien dejó el paquete):
                                </span>
                                <p className="font-bold text-gray-800 text-sm">
                                    {entregaExitosaData.remitente_nombre} {entregaExitosaData.remitente_apellido}
                                </p>
                                <p className="text-gray-500">CI/NIT: {entregaExitosaData.remitente_ci_nit || 'N/A'} {entregaExitosaData.remitente_telefono ? `| Tel: ${entregaExitosaData.remitente_telefono}` : ''}</p>
                            </div>

                            {/* 2. Recepcionista */}
                            <div className="pb-2 border-b border-gray-200/70">
                                <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">
                                    2. Personal de Recepción (Atendido por):
                                </span>
                                <p className="font-bold text-blue-800 text-sm">
                                    {entregaExitosaData.usuario_entrega_nombre || recepcionistaNombre}
                                </p>
                            </div>

                            {/* 3. Receptor */}
                            <div>
                                <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">
                                    3. Persona que Recibió (Receptor):
                                </span>
                                <p className="font-bold text-emerald-800 text-sm">
                                    {entregaExitosaData.receptor_nombre} {entregaExitosaData.receptor_apellido || ''}
                                </p>
                                <p className="text-gray-500">CI/NIT: {entregaExitosaData.receptor_ci_nit} {entregaExitosaData.receptor_telefono ? `| Tel: ${entregaExitosaData.receptor_telefono}` : ''}</p>
                            </div>
                        </div>

                        {/* Foto de Respaldo de Entrega si existe */}
                        {(imagenPreview || entregaExitosaData.imagen_entrega) && (
                            <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100 text-left">
                                <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider mb-1.5">
                                    Foto de Respaldo de Entrega:
                                </span>
                                <div className="h-40 rounded-xl overflow-hidden bg-black/5 border flex items-center justify-center">
                                    <img 
                                        src={imagenPreview || (typeof entregaExitosaData.imagen_entrega === 'string' ? entregaExitosaData.imagen_entrega : '')} 
                                        alt="Respaldo Entrega" 
                                        className="max-h-full max-w-full object-contain" 
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleImprimir}
                                className="flex-1 py-3 px-4 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center gap-2 transition-colors"
                            >
                                <Printer className="w-4 h-4" />
                                Imprimir Comprobante
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 py-3 px-4 text-xs font-bold text-white bg-gradient-to-r from-[#3564db] to-[#92b1ed] hover:shadow-lg rounded-xl transition-all"
                            >
                                Cerrar y Finalizar
                            </button>
                        </div>
                    </div>
                ) : (
                    /* VISTA 2: Formulario de Validación de Entrega */
                    <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 custom-scroll"><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                        {/* Alerta de error */}
                        {errorMsg && (
                            <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-2.5 text-xs text-red-700 animate-fade-in">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 font-medium">{errorMsg}</div>
                            </div>
                        )}

                        {/* LAS 3 PARTES DETALLADAS */}
                        <div className="space-y-3">
                            {/* 1. Remitente (Quien dejó el paquete) */}
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-2xl">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    <User className="w-3.5 h-3.5 text-gray-500" />
                                    1. Quien Dejó el Paquete (Remitente)
                                </div>
                                <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                                    <div>
                                        <span className="text-gray-400 block text-[10px]">Nombre:</span>
                                        <p className="font-bold text-gray-900">{correspondencia.remitente_nombre} {correspondencia.remitente_apellido}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block text-[10px]">CI / NIT:</span>
                                        <p className="font-semibold text-gray-800">{correspondencia.remitente_ci_nit || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Personal de Recepción (Atendido por) */}
                            <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-2xl flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-[#3564db]/10 text-[#3564db] rounded-lg">
                                        <Building2 className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">
                                            2. Atendido por (Recepción)
                                        </span>
                                        <p className="font-bold text-[#3564db] text-xs">
                                            {recepcionistaNombre}
                                        </p>
                                    </div>
                                </div>
                                <span className="px-2 py-0.5 bg-blue-100 text-[#3564db] text-[10px] font-bold rounded-full">
                                    Automático
                                </span>
                            </div>

                            {/* 3. Persona que Retira (Receptor) */}
                            <div className="p-3.5 bg-emerald-50/50 border border-emerald-200 rounded-2xl space-y-2.5">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-900 uppercase tracking-wider">
                                    <UserCheck className="w-4 h-4 text-emerald-600" />
                                    3. Persona que Retira el Paquete (Receptor)
                                </div>
                                <div className="grid grid-cols-2 gap-2.5">
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Nombre Completo *</label>
                                        <input
                                            type="text"
                                            value={receptorNombre}
                                            onChange={(e) => setReceptorNombre(e.target.value)}
                                            placeholder="Nombre de quien recibe"
                                            className="w-full px-3 py-2 text-xs bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none font-medium"
                                            required
                                        />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">CI / NIT *</label>
                                        <input
                                            type="text"
                                            value={receptorCiNit}
                                            onChange={(e) => setReceptorCiNit(e.target.value)}
                                            placeholder="Nro. Documento"
                                            className="w-full px-3 py-2 text-xs bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none font-medium"
                                            required
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Teléfono (Opcional)</label>
                                        <input
                                            type="text"
                                            value={receptorTelefono}
                                            onChange={(e) => setReceptorTelefono(e.target.value)}
                                            placeholder="Teléfono de contacto"
                                            className="w-full px-3 py-2 text-xs bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none font-medium"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Campo obligatorio de PIN de Seguridad (Auto-llenado al escanear QR) */}
                        <div className="bg-gradient-to-r from-blue-50 to-teal-50 border-2 border-[#3564db]/30 rounded-2xl p-4 space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider">
                                    PIN Secreto de Retiro *
                                </label>
                                {correspondencia.pin_seguridad && pinIngresado === correspondencia.pin_seguridad && (
                                    <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" /> PIN Validado Automáticamente
                                    </span>
                                )}
                            </div>
                            <div className="relative">
                                <KeyRound className="w-5 h-5 absolute left-3.5 top-1/2 transform -translate-y-1/2 text-[#3564db]" />
                                <input
                                    type="text"
                                    maxLength={6}
                                    value={pinIngresado}
                                    onChange={(e) => setPinIngresado(e.target.value.replace(/\D/g, ''))}
                                    placeholder="PIN de 6 dígitos"
                                    className="w-full pl-11 pr-4 py-3 text-xl font-mono tracking-widest font-black text-[#3564db] bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-[#3564db] focus:border-[#3564db] outline-none shadow-inner text-center"
                                    required
                                />
                            </div>
                        </div>

                        {/* Foto de Entrega (Opcional - Desde máquina o Cámara en vivo) */}
                        <div className="space-y-2 pt-1 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                                    <Camera className="w-3.5 h-3.5 text-[#3564db]" />
                                    Foto de Respaldo de Entrega <span className="text-[10px] text-gray-400 font-normal">(Opcional)</span>
                                </label>
                                {imagenPreview && (
                                    <button
                                        type="button"
                                        onClick={handleRemovePhoto}
                                        className="text-[11px] text-red-500 hover:text-red-700 flex items-center gap-1 font-semibold"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        Eliminar foto
                                    </button>
                                )}
                            </div>

                            {/* Modo 1: Cámara en vivo para tomar snapshot */}
                            {modoCamaraEnVivo && (
                                <div className="relative rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center border-2 border-[#92b1ed]">
                                    <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
                                    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={capturePhoto}
                                            className="px-4 py-2 bg-gradient-to-r from-[#3564db] to-[#92b1ed] text-white rounded-xl text-xs font-bold shadow-lg flex items-center gap-1.5 hover:scale-105 transition-transform"
                                        >
                                            <Camera className="w-4 h-4" />
                                            Capturar Foto Ahora
                                        </button>
                                        <button
                                            type="button"
                                            onClick={cancelLiveCamera}
                                            className="px-3 py-2 bg-black/60 text-white rounded-xl text-xs font-semibold hover:bg-black/80 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Modo 2: Foto capturada o seleccionada */}
                            {!modoCamaraEnVivo && imagenPreview && (
                                <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 h-36 flex items-center justify-center group">
                                    <img src={imagenPreview} alt="Foto Entrega" className="w-full h-full object-contain" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                                        <button
                                            type="button"
                                            onClick={startLiveCamera}
                                            className="p-2 bg-white text-gray-700 rounded-xl text-xs font-bold shadow hover:bg-gray-100"
                                        >
                                            Tomar otra foto
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleRemovePhoto}
                                            className="p-2 bg-red-600 text-white rounded-xl text-xs font-bold shadow hover:bg-red-700"
                                        >
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Modo 3: Botones para elegir opción (Cámara o Subir) */}
                            {!modoCamaraEnVivo && !imagenPreview && (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={startLiveCamera}
                                        className="py-3 px-3 border border-dashed border-[#3564db]/40 hover:border-[#3564db] bg-blue-50/50 hover:bg-blue-50 text-[#3564db] rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all"
                                    >
                                        <Camera className="w-5 h-5" />
                                        <span className="text-xs font-bold">Tomar Foto con Cámara</span>
                                        <span className="text-[10px] text-gray-500">Capturar ese momento</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="py-3 px-3 border border-dashed border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all"
                                    >
                                        <Upload className="w-5 h-5 text-gray-500" />
                                        <span className="text-xs font-bold">Adjuntar de la Máquina</span>
                                        <span className="text-[10px] text-gray-500">Seleccionar archivo</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Botones de acción */}
                        <div className="flex gap-3 pt-4 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="flex-1 py-3 px-4 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting || !pinIngresado.trim()}
                                className="flex-1 py-3 px-4 text-xs font-bold text-white bg-gradient-to-r from-[#3564db] to-[#92b1ed] hover:shadow-lg rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Validando...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-4 h-4" />
                                        Validar y Confirmar Entrega
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );
};

export default ValidarEntregaModal;

